import Peer from 'peerjs';

/**
 * supportSession — thin wrapper around PeerJS that gives us the
 * primitives the Connect for Support UI needs:
 *   • generate / redeem a short 6-char session code (CHK-XXXX)
 *   • create a Peer bound to that code
 *   • connect to a remote peer for chat (data channel)
 *   • call a remote peer with a media stream (voice + screen share)
 *
 * All traffic is peer-to-peer over WebRTC once the handshake completes;
 * PeerJS' free public cloud only relays the initial SDP/ICE exchange.
 * If we ever outgrow the free tier we can point `host`/`port`/`path`
 * at a self-hosted PeerJS server without touching the callers.
 *
 * SESSION CODE FORMAT
 *   Human code : `CHK-XXXX`   (uppercase, no confusing chars)
 *   Peer ID    : `chanakya-support-CHK-XXXX`
 * The prefix namespaces our IDs inside the shared PeerJS Cloud pool so
 * we won't ever collide with an unrelated app running on the same
 * signaling server.
 */

const PEER_PREFIX  = 'chanakya-support-';
// Removed confusables (0/O, 1/I, L) so codes are safe to dictate over
// the phone.
const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
const CODE_LENGTH   = 4;

export function generateSessionCode() {
  let out = '';
  const arr = new Uint32Array(CODE_LENGTH);
  (window.crypto || window.msCrypto).getRandomValues(arr);
  for (let i = 0; i < CODE_LENGTH; i++) {
    out += CODE_ALPHABET[arr[i] % CODE_ALPHABET.length];
  }
  return `CHK-${out}`;
}

/** Normalize any user-typed code to the canonical `CHK-XXXX` form. */
export function normalizeSessionCode(input) {
  if (!input) return '';
  const clean = String(input).toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (clean.length === 0) return '';
  const body = clean.startsWith('CHK') ? clean.slice(3) : clean;
  return body ? `CHK-${body}` : 'CHK-';
}

export function codeToPeerId(code) {
  return `${PEER_PREFIX}${code}`;
}

/**
 * Create a Peer with a specific ID. Returns a promise that resolves
 * once PeerJS reports the connection is open (or rejects on error).
 * Callers should keep a reference to the peer to close it on unmount.
 */
export function createPeer(id) {
  return new Promise((resolve, reject) => {
    const peer = new Peer(id, {
      // PeerJS free cloud (https://peerjs.com/peerserver.html) — no
      // account or key required.
      debug: 1,
    });
    let settled = false;
    peer.on('open', (openId) => {
      if (settled) return;
      settled = true;
      resolve({ peer, id: openId });
    });
    peer.on('error', (err) => {
      if (settled) return;
      settled = true;
      peer.destroy();
      reject(err);
    });
  });
}
